import router from "page";

import { addToCart, removeFromCart } from "./utils.js";
import { fetchGet } from "./utilsFetch.js";
import { getLocaleResource } from "./locales.js";

import { get } from "svelte/store";

import {
    locale,
    showReservationModal,
    reservationProduct,
    isSales,
    salesCompanyKey,
} from "../stores.js";

import OrderInput from "../components/OrderInput.svelte";

export function keywordFilter(headerValue, rowValue, rowData, filterParams) {
    let keywords = [];
    if (headerValue) {
        keywords = headerValue.toLowerCase().split(" ");
    }
    let keywordsFound = [];

    keywords.forEach((keyword) => {
        keywordsFound.push(rowValue.toLowerCase().includes(keyword));
    });

    let allKeywordsFound = keywordsFound.every((keyword) => keyword);

    return allKeywordsFound;
}

export function dateFormatter(cell, formatterParams) {
    return dateFormatterSimple(cell.getValue());
}

export function dateFormatterSimple(date) {
    if (!date) return;
    let dateParts = date.split("T")[0].split("-");
    let formattedDate = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
    return formattedDate;
}

export function dateTimeFormatter(datetime) {
    if (!datetime) return;
    let parts = datetime.split("T");
    let dateParts = parts[0].split("-");
    let formattedDate = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;

    let time = parts[1].split(".")[0];

    return `${formattedDate} ${time}`;
}

export function productPictureLinkFormatter(cell, formatterParams) {
    let url = cell.getValue();
    return `
        <a href="${url}" target="_blank" style="height: ${formatterParams.height}; width: ${formatterParams.width}; display: flex; justify-content: center;">
            <img src="${url}" style="height: auto; width: auto; max-width: 100%; max-height: 100%;}" />
        </a>
    `;
}

export function numberFormatterSimple(number) {
    return parseFloat(number).toLocaleString();
}

export function numberFormatter(cell, formatterParams) {
    return numberFormatterSimple(cell.getValue());
}

export function currencyFormatter(cell, formatterParams) {
    let value = `${numberFormatterSimple(cell.getValue())} ${
        formatterParams.currency
    }`;
    return value;
}

export function oneDecimalFormatter(cell, formatterParams) {
    return cell.getValue().toFixed(1);
}

export function percentFormatter(cell, formatterParams) {
    return `${cell.getValue()}%`;
}

function onCellClickGoToProduct(e, cell) {
    let data = cell._cell.row.data;
    let sku = data.sku;

    let url = `/products/${sku}`;

    window.open(generateUrl(url), "_blank");
}

function generateUrl(url) {
    // Check if we need to add the salesCompanyKey parameter
    if (
        !url.includes("salesCompanyKey") &&
        get(isSales) &&
        get(salesCompanyKey)
    ) {
        // Determine whether the URL already has query parameters
        const hasQuery = url.includes("?");

        // Ensure the URL ends with '/' before query parameters if it doesn't have any
        if (!hasQuery && !url.endsWith("/")) {
            url += "/";
        }

        // Append the parameter correctly based on whether there are existing query parameters
        if (hasQuery) {
            url += `&salesCompanyKey=${get(salesCompanyKey)}`;
        } else {
            url += `?salesCompanyKey=${get(salesCompanyKey)}`;
        }
    }

    return url;
}

let columnImage = {
    title: "",
    field: "img",
    formatter: productPictureLinkFormatter,
    formatterParams: {
        width: "50px",
        height: "50px",
    },
    vertAlign: "middle",
};

let columnSku = {
    title: getLocaleResource(locale, "sku"),
    field: "sku",
    headerFilter: "input",
    headerFilterFunc: keywordFilter,
    headerFilterPlaceholder: "⌕",
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnProduct = {
    title: getLocaleResource(locale, "product"),
    field: "productName",
    width: 200,
    headerFilter: "input",
    headerFilterFunc: keywordFilter,
    headerFilterPlaceholder: "⌕",
    formatter: (cell, formatterParams, onRendered) => {
        cell.getElement().style.fontWeight = "bold";
        cell.getElement().style.whiteSpace = "pre-wrap";

        return cell.getValue();
    },
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnVat = {
    title: getLocaleResource(locale, "vat"),
    field: "vat",
    formatter: percentFormatter,
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnPrice = {
    title: `${getLocaleResource(locale, "price")}<br>${getLocaleResource(
        locale,
        "withoutVat"
    )}`,
    field: "priceWithoutVat",
    formatter: currencyFormatter,
    formatterParams: { currency: "KM" },
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnDiscount = {
    title: getLocaleResource(locale, "discount"),
    field: "totalDiscount",
    formatter: percentFormatter,
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnFinalPrice = {
    title: `${getLocaleResource(locale, "finalPrice")}<br>${getLocaleResource(
        locale,
        "withoutVat"
    )}`,
    field: "finalPriceWithoutVat",
    formatter: currencyFormatter,
    formatterParams: { currency: "KM" },
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnStock = {
    title: getLocaleResource(locale, "stock").split(" ").join("<br>"),
    field: "stockHidden",
    formatter: (cell) => {
        let stock = cell.getValue().split(".")[0];

        if (stock == "0") {
            cell.getElement().style.background = `rgba(255, 0, 0, 0.3)`;
        }

        return stock;
    },
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnArrivalDate = {
    title: getLocaleResource(locale, "arrival").split(" ").join("<br>"),
    field: "arrivalDate",
    vertAlign: "middle",
    formatter: dateFormatter,
    cellClick: onCellClickGoToProduct,
};

let columnPackaging = {
    title: getLocaleResource(locale, "packaging"),
    field: "packaging",
    headerFilter: "input",
    headerFilterFunc: keywordFilter,
    headerFilterPlaceholder: "⌕",
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnBarcode = {
    title: getLocaleResource(locale, "barcode"),
    field: "barcodes",
    width: 200,
    headerFilter: "input",
    headerFilterFunc: keywordFilter,
    headerFilterPlaceholder: "⌕",
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnFitting = {
    title: getLocaleResource(locale, "fitting"),
    field: "fitting",
    width: 100,
    headerFilter: "input",
    headerFilterFunc: keywordFilter,
    headerFilterPlaceholder: "⌕",
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

let columnOrder = {
    title: getLocaleResource(locale, "order"),
    field: "cartQuantity",
    editor: "input",
    formatter: (cell, formatterParams) => {
        let data = cell._cell.row.data;

        if (data.stock == "0") {
            cell.getElement().style.cssText += `
                background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' height='20px' width='120px'><text x='0' y='15' font-size='14' font-family='Sans,Arial' fill='black' fill-opacity='0.2'>${getLocaleResource(
                    locale,
                    "reservation"
                )}</text></svg>") !important; 
                background-repeat: no-repeat !important;
                background-position: 10px 50% !important;
            `;
        }

        return cell.getValue();
    },
    cellEdited: async (cell) => {
        let sku = cell._cell.row.data.sku;
        let packaging = cell._cell.row.data.packaging;

        let oldValue = cell._cell.oldValue;
        let value = cell._cell.value;

        let isNotInput = isNaN(parseInt(oldValue)) && isNaN(parseInt(value));

        if (isNotInput) return;

        await addToCart(sku, value, packaging);
    },
    vertAlign: "middle",
    frozen: true,
};

let columnQuantity = {
    title: getLocaleResource(locale, "quantity"),
    field: "quantity",
    formatter: numberFormatter,
    vertAlign: "middle",
    cellClick: onCellClickGoToProduct,
};

export let productsTableColumns = [
    { title: "#", field: "", formatter: "rownum", vertAlign: "middle" },
    columnImage,
    columnSku,
    columnProduct,
    columnFitting,
    columnPrice,
    columnDiscount,
    columnFinalPrice,
    // {
    //     title: "Цена",
    //     columns: [
    //         {
    //             title: "со ДДВ",
    //             field: "priceWithVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //             vertAlign: "middle",
    //         },
    //         {
    //             title: "без ДДВ",
    //             field: "priceWithoutVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //             vertAlign: "middle",
    //         },
    //     ],
    // },

    // {
    //     title: "Ваша цена",
    //     columns: [
    //         {
    //             title: "Попуст",
    //             field: "totalDiscount",
    //             formatter: percentFormatter,
    //             vertAlign: "middle",
    //         },
    //         {
    //             title: "без ДДВ",
    //             field: "finalPriceWithoutVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //             vertAlign: "middle",
    //         },
    //         {
    //             title: "со ДДВ",
    //             field: "finalPriceWithVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //             vertAlign: "middle",
    //         },
    //     ],
    // },
    columnStock,
    columnArrivalDate,
    columnPackaging,
    columnBarcode,
    columnOrder,
    // {
    //     title: "Нарачка 2",
    //     frozen: true,
    //     formatter: orderInputColumn,
    // },
    // {
    //     title: "Нарачка",
    //     frozen: true,
    //     formatter: addToCartFormatter,
    // },
];

export let cartTableColumns = [
    { title: "#", field: "", formatter: "rownum", vertAlign: "middle" },
    columnImage,
    columnSku,
    {
        ...columnProduct,
        formatter: (cell, formatterParams, onRendered) => {
            cell.getElement().style.fontWeight = "bold";
            cell.getElement().style.whiteSpace = "pre-wrap";

            let data = cell._cell.row.data;

            let res = `[<span style="color: red;">${getLocaleResource(
                locale,
                "reservation"
            ).toUpperCase()}</span>]`;

            return `<span>${
                data.isReservation ? res : ""
            } ${cell.getValue()}</span>`;
        },
    },
    columnPrice,
    columnDiscount,
    columnFinalPrice,
    {
        title: `${getLocaleResource(locale, "total")}<br>${getLocaleResource(
            locale,
            "withoutVat"
        )}`,
        field: "cartTotalWithoutVat",
        formatter: currencyFormatter,
        formatterParams: { currency: "KM" },
        // topCalc: "sum",
        // topCalcFormatter: currencyFormatter,
        // topCalcFormatterParams: { currency: "KM" },
        vertAlign: "middle",
        cellClick: onCellClickGoToProduct,
    },
    columnStock,
    columnArrivalDate,
    columnPackaging,
    columnBarcode,
    {
        title: getLocaleResource(locale, "order"),
        field: "cartQuantity",
        editor: "input",
        vertAlign: "center",
        cellEdited: async (cell) => {
            let table = cell._cell.table;
            let sku = cell._cell.row.data.sku;
            let value = cell._cell.value;

            await addToCart(sku, parseInt(value) || 0);

            let cart = await fetchGet(`/api/cart`);
            table.replaceData(cart);
        },
        vertAlign: "middle",
        frozen: true,
    },
    {
        frozen: true,
        formatter: (cell) => {
            let deleteButton = document.createElement("div");
            deleteButton.className = "btn btn-red";
            deleteButton.innerHTML = `<i class="fas fa-times no-margin">`;

            deleteButton.addEventListener("click", async () => {
                let table = cell._cell.table;
                let data = cell._cell.row.data;
                let sku = data.sku;

                removeFromCart(sku);

                let cart = await fetchGet(`/api/cart`);
                table.replaceData(cart);
            });
            return deleteButton;
        },
        vertAlign: "middle",
    },
];

export let documentTableColumns = [
    { title: "#", field: "", formatter: "rownum", vertAlign: "middle" },
    columnImage,
    columnSku,
    columnProduct,
    columnQuantity,
    columnPrice,
    columnDiscount,
    columnFinalPrice,
    // {
    //     title: "Цена",
    //     columns: [
    //         {
    //             title: "со ДДВ",
    //             field: "originalPriceWithVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //         },
    //         {
    //             title: "без ДДВ",
    //             field: "originalPriceWithoutVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //         },
    //     ],
    // },
    // {
    //     title: "Ваша цена",
    //     columns: [
    //         {
    //             title: "Попуст",
    //             field: "rebate",
    //             formatter: percentFormatter,
    //         },
    //         {
    //             title: "без ДДВ",
    //             field: "clientPriceWithoutVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //         },
    //         {
    //             title: "со ДДВ",
    //             field: "clientPriceWithVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //         },
    //     ],
    // },
    {
        title: `${getLocaleResource(locale, "total")} ${getLocaleResource(
            locale,
            "withoutVat"
        )}`,
        field: "totalWithoutVat",
        formatter: currencyFormatter,
        formatterParams: { currency: "KM" },
        topCalc: "sum",
        topCalcFormatter: currencyFormatter,
        topCalcFormatterParams: { currency: "KM" },
        vertAlign: "middle",
    },
    // {
    //     title: getLocaleResource(locale, "total"),
    //     columns: [
    //         {
    //             title: getLocaleResource(locale, "withoutVat"),
    //             field: "totalWithoutVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //             topCalc: "sum",
    //             topCalcFormatter: numberFormatter,
    //             vertAlign: "middle",
    //         },
    //         {
    //             title: getLocaleResource(locale, "withVat"),
    //             field: "totalWithVat",
    //             formatter: currencyFormatter,
    //             formatterParams: { currency: "KM" },
    //             topCalc: "sum",
    //             topCalcFormatter: numberFormatter,
    //             vertAlign: "middle",
    //         },
    //     ],
    // },
    columnStock,
    columnArrivalDate,
    columnPackaging,
    columnOrder,
];

let columnsOnSale = {
    title: getLocaleResource(locale, "onSale"),
    columns: [
        {
            title: getLocaleResource(locale, "from"),
            field: "onSaleFromDate",
            headerFilter: "input",
            headerFilterFunc: keywordFilter,
            headerFilterPlaceholder: "⌕",
            vertAlign: "middle",
            formatter: dateFormatter,
            cellClick: onCellClickGoToProduct,
        },
        {
            title: getLocaleResource(locale, "to"),
            field: "onSaleToDate",
            headerFilter: "input",
            headerFilterFunc: keywordFilter,
            headerFilterPlaceholder: "⌕",
            vertAlign: "middle",
            formatter: dateFormatter,
            cellClick: onCellClickGoToProduct,
        },
    ],
};

export let productsOnSaleTableColumns = [
    { title: "#", field: "", formatter: "rownum", vertAlign: "middle" },
    columnImage,
    columnSku,
    columnProduct,
    columnPrice,
    columnDiscount,
    columnsOnSale,
    columnFinalPrice,
    columnStock,
    columnArrivalDate,
    columnBarcode,
    columnOrder,
];

// export function insertTableColumnAfter(columns, previousColumnName, newColumn) {
//     // prevent double insertions
//     let exists = columns.filter((c) => c.field == newColumn.field).length > 0;

//     if (exists) return;

//     let previousColumnNameIndex = columns
//         .map((e) => e.title.toLowerCase())
//         .indexOf(previousColumnName.toLowerCase());

//     // inserts after index
//     columns.splice(previousColumnNameIndex + 1, 0, newColumn);
// }

// export function addToCartFormatter(cell) {
//     let data = cell._cell.row.data;

//     let cartQuantity = data.cartQuantity;
//     let sku = data.sku;

//     let inputGroup = document.createElement("div");
//     inputGroup.className = "input-group";

//     let input = document.createElement("input");
//     input.value = cartQuantity;

//     // input.addEventListener("keyup", async (e) => {
//     //     // if (e.key == "Enter") {
//     //     addToCart(sku, input.value);
//     //     // }
//     // });

//     input.addEventListener("click", (e) => {
//         input.focus();
//     });

//     input.addEventListener("input", (e) => {
//         updateValue(e.target.value);
//     });

//     const updateValue = debounce((value) => {
//         let val = value ? parseInt(value) : 0;
//         addToCart(sku, val);
//     }, 0);

//     function debounce(callback, delay = 1000) {
//         let timeout;

//         return (...args) => {
//             clearTimeout(timeout);
//             timeout = setTimeout(() => {
//                 callback(...args);
//             }, delay);
//         };
//     }

//     inputGroup.appendChild(input);

//     // let button = document.createElement("button");
//     // button.className = "btn btn-blue";
//     // button.innerHTML = '<i class="fas fa-plus"></i>';

//     // button.addEventListener("click", async () => {
//     //     addToCart(sku, input.value);
//     // });

//     // inputGroup.appendChild(button);

//     return inputGroup;
// }

// function orderInputColumn(cell, formatterParams, onRendered) {
//     // Need to wait for the cell to be rendered so cell dom and it's data exist.
//     onRendered(() => {
//         // Render Svelte component into Tabulator's cell.
//         // stopButton,logsButton variables are never used.
//         let data = cell._cell.row.data;

//         new OrderInput({
//             target: cell.getElement(),
//             props: {
//                 props: { sku: data.sku },
//                 value: data.cartQuantity,
//                 callback: addToCart,
//             },
//         });
//     });
// }

// const searchPlaceholder = "⌕";

// function linkFormatter(cell, formatterParams) {
//     cell._cell.element.style.color = "#547dc1";
//     return cell.getValue();
// }

// function highlightTitleFormatter(cell, formatterParams) {
//     cell.getElement().style.color = "red";
//     return cell.getValue();
// }
